import { useState, useCallback, useRef, useEffect } from 'react';
import {
  CORRECT_FEEDBACK_MS,
  WRONG_FEEDBACK_MS,
  COMBO_SET_CHANGE,
  getLevelDuration,
  getDifferentSymbolSet,
  generateSymbolMap,
  shuffleNumbers,
  calculateScore,
  pickNextSymbol,
  getRandomSymbolSetForLevel,
  type QuestionRecord,
  type DsstGameData,
  type Feedback,
} from '@/pages/dsst-spick/dsstConfig';
import {
  unlockAudio,
  playCorrectSound,
  playWrongSound,
} from '@/pages/dsst-spick/utils/sound';

// 스픽(Spick) 버전 플로우:
// 게임 소개 → 카운트다운 → 본게임(총 45초, 단일 스테이지) → 결과
// 연습 단계/스테이지 구분은 없으며, 기호 세트는 게임 시작 시 무작위로 하나 사용한다.
// 5문제 이상 연속 정답을 이어가면 기호 그룹이 다른 세트로 교체된다.
export type DsstScreen = 'guide' | 'countdown' | 'game' | 'result';

export function useGameState() {
  const [screen, setScreen] = useState<DsstScreen>('guide');
  const [symbolMap, setSymbolMap] = useState<number[]>([]);
  const [symbolSetIndex, setSymbolSetIndex] = useState(() => getRandomSymbolSetForLevel(0));
  const [currentSymbol, setCurrentSymbol] = useState(0);
  const [selectedNumber, setSelectedNumber] = useState<number | null>(null);
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [remainingSeconds, setRemainingSeconds] = useState(getLevelDuration(0));
  const [score, setScore] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [wrongCount, setWrongCount] = useState(0);
  const [combo, setCombo] = useState(0);
  const [setChangeNotice, setSetChangeNotice] = useState(false);
  const [numberOrder, setNumberOrder] = useState<number[]>([1, 2, 3, 4, 5, 6]);
  const [showExitModal, setShowExitModal] = useState(false);

  const symbolMapRef = useRef<number[]>([]);
  const symbolSetIndexRef = useRef(symbolSetIndex);
  const currentSymbolRef = useRef(0);
  const questionStartRef = useRef(0);
  const endTimeRef = useRef(0);
  const timerRef = useRef<number | null>(null);
  const feedbackTimerRef = useRef<number | null>(null);
  const noticeTimerRef = useRef<number | null>(null);
  const historyRef = useRef<number[]>([]);
  const questionsRef = useRef<QuestionRecord[]>([]);
  const correctRef = useRef(0);
  const wrongRef = useRef(0);
  const comboRef = useRef(0);
  const isAnsweringRef = useRef(false);
  const isActiveRef = useRef(false);
  const onTimeUpRef = useRef<() => void>(() => {});

  const clearTimers = useCallback(() => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (feedbackTimerRef.current !== null) {
      clearTimeout(feedbackTimerRef.current);
      feedbackTimerRef.current = null;
    }
    if (noticeTimerRef.current !== null) {
      clearTimeout(noticeTimerRef.current);
      noticeTimerRef.current = null;
    }
  }, []);

  useEffect(() => clearTimers, [clearTimers]);

  const setCurrentSymbolBoth = useCallback((s: number) => {
    currentSymbolRef.current = s;
    setCurrentSymbol(s);
  }, []);

  const applySetIndex = useCallback((set: number) => {
    symbolSetIndexRef.current = set;
    setSymbolSetIndex(set);
  }, []);

  // 연속 정답 마일스톤 도달 시 기호 그룹을 다른 세트로 교체한다.
  const switchSymbolSet = useCallback(() => {
    const next = getDifferentSymbolSet(symbolSetIndexRef.current);
    applySetIndex(next);
    setSetChangeNotice(true);
    if (noticeTimerRef.current !== null) clearTimeout(noticeTimerRef.current);
    noticeTimerRef.current = window.setTimeout(() => setSetChangeNotice(false), 1400);
  }, [applySetIndex]);

  const finishGame = useCallback(
    (aborted = false) => {
      clearTimers();
      isActiveRef.current = false;
      isAnsweringRef.current = false;

      const correct = correctRef.current;
      const wrong = wrongRef.current;
      const total = correct + wrong;
      const finalScore = calculateScore(correct, wrong);
      const questions = questionsRef.current;

      const avgReaction =
        questions.length > 0
          ? Math.round(
              questions.reduce((s, q) => s + q.reactionTimeMs, 0) / questions.length,
            )
          : 0;

      const symbolAccuracy: Record<number, { correct: number; total: number; rate: number }> = {};
      const symbolSumRt: Record<number, { sum: number; count: number }> = {};
      for (let i = 0; i < 6; i++) {
        symbolAccuracy[i] = { correct: 0, total: 0, rate: 0 };
        symbolSumRt[i] = { sum: 0, count: 0 };
      }
      questions.forEach((q) => {
        symbolAccuracy[q.symbolId].total += 1;
        if (q.isCorrect) symbolAccuracy[q.symbolId].correct += 1;
        symbolSumRt[q.symbolId].sum += q.reactionTimeMs;
        symbolSumRt[q.symbolId].count += 1;
      });
      for (let i = 0; i < 6; i++) {
        const a = symbolAccuracy[i];
        a.rate = a.total > 0 ? Math.round((a.correct / a.total) * 1000) / 10 : 0;
      }
      const symbolAvgReactionMs: Record<number, number> = {};
      for (let i = 0; i < 6; i++) {
        const s = symbolSumRt[i];
        symbolAvgReactionMs[i] = s.count > 0 ? Math.round(s.sum / s.count) : 0;
      }

      const gameData: DsstGameData = {
        gameType: 'dsst',
        performedAt: new Date().toISOString(),
        totalQuestions: total,
        correctCount: correct,
        wrongCount: wrong,
        accuracy: total > 0 ? Math.round((correct / total) * 1000) / 10 : 0,
        questions,
        averageReactionTimeMs: avgReaction,
        symbolAccuracy,
        symbolAvgReactionMs,
        score: finalScore,
        isAborted: aborted,
      };

      setScore(finalScore);
      setTotalQuestions(total);
      setCorrectCount(correct);
      setWrongCount(wrong);
      setRemainingSeconds(0);
      console.log('DSST(스픽) 본게임 데이터:', gameData);
      setScreen('result');
    },
    [clearTimers],
  );

  // 게임 시작: 심볼 세트(랜덤)를 정하고 카운트다운 화면으로 이동
  const startLevel = useCallback(
    (presetSet?: number) => {
      clearTimers();
      unlockAudio();
      isActiveRef.current = false;
      isAnsweringRef.current = false;

      applySetIndex(presetSet ?? getRandomSymbolSetForLevel(0));

      setSelectedNumber(null);
      setFeedback(null);
      setSetChangeNotice(false);
      comboRef.current = 0;
      setCombo(0);

      questionsRef.current = [];
      correctRef.current = 0;
      wrongRef.current = 0;
      setCorrectCount(0);
      setWrongCount(0);
      setTotalQuestions(0);

      setRemainingSeconds(getLevelDuration(0));
      setScreen('countdown');
    },
    [clearTimers, applySetIndex],
  );

  const startTimer = useCallback((duration: number) => {
    endTimeRef.current = Date.now() + duration * 1000;
    setRemainingSeconds(duration);
    timerRef.current = window.setInterval(() => {
      const remaining = endTimeRef.current - Date.now();
      if (remaining <= 0) {
        setRemainingSeconds(0);
        onTimeUpRef.current();
      } else {
        setRemainingSeconds(Math.ceil(remaining / 1000));
      }
    }, 100);
  }, []);

  const showNextQuestion = useCallback(() => {
    const next = pickNextSymbol(historyRef.current);
    historyRef.current = [...historyRef.current, next];
    setCurrentSymbolBoth(next);
    setSelectedNumber(null);
    setFeedback(null);
    // 본게임에서만 숫자 패널 위치를 랜덤으로 변경
    if (isActiveRef.current) {
      setNumberOrder(shuffleNumbers());
    }
    isAnsweringRef.current = false;
    questionStartRef.current = Date.now();
  }, [setCurrentSymbolBoth]);

  // 카운트다운 종료 → 본게임 시작 (총 45초 단일 스테이지)
  const handleCountdownComplete = useCallback(() => {
    const map = generateSymbolMap();
    symbolMapRef.current = map;
    setSymbolMap(map);

    historyRef.current = [];
    const first = pickNextSymbol([]);
    historyRef.current = [first];
    setCurrentSymbolBoth(first);

    setNumberOrder([1, 2, 3, 4, 5, 6]);
    setSelectedNumber(null);
    setFeedback(null);
    setSetChangeNotice(false);
    comboRef.current = 0;
    setCombo(0);
    isAnsweringRef.current = false;
    isActiveRef.current = true;
    questionStartRef.current = Date.now();

    setScreen('game');
    startTimer(getLevelDuration(0));
  }, [setCurrentSymbolBoth, startTimer]);

  // 제한시간 종료 → 결과 화면
  const handleTimeUp = useCallback(() => {
    finishGame();
  }, [finishGame]);

  useEffect(() => {
    onTimeUpRef.current = handleTimeUp;
  }, [handleTimeUp]);

  const handleGameSelect = useCallback(
    (number: number) => {
      if (!isActiveRef.current || isAnsweringRef.current) return;
      if (Date.now() >= endTimeRef.current) return; // 제한시간 초과 시 입력 차단
      isAnsweringRef.current = true;

      const map = symbolMapRef.current;
      const symbolId = currentSymbolRef.current;
      const correctAnswer = map[symbolId];
      const isCorrect = number === correctAnswer;
      const reactionTimeMs = Date.now() - questionStartRef.current;

      if (isCorrect) playCorrectSound();
      else playWrongSound();

      const record: QuestionRecord = {
        level: 0,
        symbolId,
        answer: number,
        correctAnswer,
        isCorrect,
        reactionTimeMs,
      };
      questionsRef.current = [...questionsRef.current, record];

      if (isCorrect) {
        correctRef.current += 1;
        setCorrectCount(correctRef.current);
        comboRef.current += 1;
        setCombo(comboRef.current);
        // 5문제 이상 연속 정답일 때마다 기호 그룹 교체
        if (comboRef.current % COMBO_SET_CHANGE === 0) {
          switchSymbolSet();
        }
      } else {
        wrongRef.current += 1;
        setWrongCount(wrongRef.current);
        comboRef.current = 0;
        setCombo(0);
      }
      setTotalQuestions(questionsRef.current.length);

      setSelectedNumber(number);
      setFeedback(isCorrect ? 'correct' : 'wrong');

      feedbackTimerRef.current = window.setTimeout(() => {
        showNextQuestion();
      }, isCorrect ? CORRECT_FEEDBACK_MS : WRONG_FEEDBACK_MS);
    },
    [showNextQuestion, switchSymbolSet],
  );

  const openExitModal = useCallback(() => setShowExitModal(true), []);
  const closeExitModal = useCallback(() => setShowExitModal(false), []);

  // 게임 소개 화면으로 안전하게 복귀 (진행 중 타이머/입력 모두 정리)
  const goToGuide = useCallback(() => {
    clearTimers();
    isActiveRef.current = false;
    isAnsweringRef.current = false;
    comboRef.current = 0;
    setCombo(0);
    setSetChangeNotice(false);
    applySetIndex(getRandomSymbolSetForLevel(0));
    setFeedback(null);
    setSelectedNumber(null);
    setScreen('guide');
  }, [clearTimers, applySetIndex]);

  const confirmExit = useCallback(() => {
    setShowExitModal(false);
    goToGuide();
  }, [goToGuide]);

  // 게임 소개에서 시작: 소개 화면에 보여준 세트를 게임에서 그대로 사용
  const startGame = useCallback(() => {
    startLevel(symbolSetIndexRef.current);
  }, [startLevel]);

  // 결과 화면 "다시하기": 새 세트로 재시작
  const retryGame = useCallback(() => {
    startLevel();
  }, [startLevel]);

  const goToNext = useCallback(() => {
    // TODO: 다음 단계(음성 인지건강 체크)로 이동
  }, []);

  return {
    screen,
    symbolMap,
    symbolSetIndex,
    currentSymbol,
    selectedNumber,
    feedback,
    remainingSeconds,
    score,
    totalQuestions,
    correctCount,
    wrongCount,
    combo,
    setChangeNotice,
    numberOrder,
    showExitModal,
    startGame,
    handleCountdownComplete,
    handleGameSelect,
    goToGuide,
    openExitModal,
    closeExitModal,
    confirmExit,
    retryGame,
    goToNext,
  };
}